import { useTranslation } from 'react-i18next';
import { UseFormRegister, FieldValues } from 'react-hook-form';
import { FormInput } from '../ui/FormInput';
import { AddressForm } from './AddressForm';
import { VALIDATION_PATTERNS, validateSiretMatchesSiren } from '../../constants/validation';

interface MainCompanyLocationErrors {
  siret?: { message?: string };
  address?: {
    street?: { message?: string };
    postalCode?: { message?: string };
    city?: { message?: string };
    country?: { message?: string };
    additionalInfo?: { message?: string };
  };
}

interface MainCompanyLocationFormProps<T extends FieldValues> {
  register: UseFormRegister<T>;
  errors: MainCompanyLocationErrors;
  siren?: string | undefined;
}

export const MainCompanyLocationForm = <T extends FieldValues>({
  register,
  errors,
  siren,
}: MainCompanyLocationFormProps<T>) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-text border-b pb-2">
        {t('company.section.main_location')}
      </h2>

      <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
        <div className="flex flex-col gap-4">
          <FormInput
            label={t('company.location.siret.label')}
            type="text"
            placeholder={t('company.location.siret.placeholder')}
            error={errors.siret?.message}
            required
            {...register(`mainLocation.siret` as any, {
              required: t('company.location.siret.error.required'),
              setValueAs: (value) => value?.replace(/\s/g, ''),
              pattern: {
                value: VALIDATION_PATTERNS.SIRET,
                message: t('company.location.siret.error.invalid'),
              },
              validate: {
                matchesSiren: (value: string) => {
                  if (!siren) return true;
                  return (
                    validateSiretMatchesSiren(value, siren) ||
                    t('company.location.siret.error.mismatch_siren')
                  );
                },
              },
            })}
          />

          <div className="mt-2">
            <label className="block text-sm font-medium text-text mb-2">
              {t('company.location.address.title')}
            </label>
            <AddressForm
              prefix="mainLocation.address"
              register={register}
              errors={errors.address || {}}
              showOptionalInfo={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
